/*
  De verlanglijst.

  Alles wat met opslag te maken heeft staat in `store` hieronder, en verder
  praat niets in dit bestand rechtstreeks met localStorage. Komt er later
  synchronisatie met een klantaccount bij, dan is dat blok het enige dat
  verandert — de hartjes, de teller en de verlanglijstpagina blijven zoals ze
  zijn.

  Er worden handles bewaard en geen id's: daarmee kan de verlanglijstpagina de
  kaarten opvragen via /products/[handle]?section_id=... Dat is exact dezelfde
  kaart als elders op de site, zonder API-sleutel.
*/
const store = {
  key: 'wishlist',

  read() {
    try {
      const value = JSON.parse(localStorage.getItem(this.key));
      return Array.isArray(value) ? value : [];
    } catch (error) {
      // Privémodus of onleesbare inhoud: dan begint de lijst gewoon leeg.
      return [];
    }
  },

  write(list) {
    try {
      localStorage.setItem(this.key, JSON.stringify(list));
    } catch (error) {
      // Niet kunnen bewaren mag de knop niet stukmaken.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  },
};

/*
  Een bewaard sieraad kan tijdelijk op concept staan. De browser weet alleen
  wélke handles bewaard zijn, niet of ze nog online staan — dus dat vragen we
  na bij de winkel: /products/[handle].js geeft 404 zodra hij eruit is.

  De uitkomst blijft in sessionStorage staan, dus het gebeurt één keer per
  bezoek en niet op elke pagina opnieuw. Gaat het opvragen mis, dan telt het
  sieraad gewoon mee: liever één te veel dan iets verbergen dat er wél is.
*/
const known = {
  key: 'wishlist-available',

  read() {
    try {
      return JSON.parse(sessionStorage.getItem(this.key)) || {};
    } catch (error) {
      return {};
    }
  },

  write(map) {
    try {
      sessionStorage.setItem(this.key, JSON.stringify(map));
    } catch (error) {
      // Privémodus: dan controleren we het volgende keer opnieuw.
    }
  },
};

async function available(list) {
  const map = known.read();
  const unchecked = list.filter((handle) => !(handle in map));

  if (unchecked.length > 0) {
    const root = window.Shopify?.routes?.root || '/';

    await Promise.all(
      unchecked.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          map[handle] = response.ok;
        } catch (error) {
          map[handle] = true;
        }
      })
    );

    known.write(map);
  }

  return list.filter((handle) => map[handle] !== false);
}

function toggle(handle) {
  const list = store.read();
  const index = list.indexOf(handle);

  if (index === -1) {
    list.push(handle);
  } else {
    list.splice(index, 1);
  }

  store.write(list);
}

/*
  Eén functie die alles op de pagina in lijn brengt met de opslag. Zo hoeft een
  knop niet te weten wat er elders staat: verandert er iets, dan wordt gewoon
  alles opnieuw gezet. Ook nodig voor kaarten die later binnenkomen, zoals de
  aanbevelingen onder een product.
*/
function sync() {
  const list = store.read();

  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const saved = list.includes(button.dataset.wishlistToggle);
    button.setAttribute('aria-pressed', saved);

    // "Bewaren" op iets dat al bewaard is klopt niet voor wie het hoort.
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  });

  count(list);
}

/*
  Het tellertje wacht op die controle en loopt dus een fractie achter op de
  hartjes. Dat is de bedoeling: een hartje moet meteen reageren op een klik,
  een getal mag even wachten tot het klopt.
*/
async function count(list) {
  const shown = list.length === 0 ? [] : await available(list);

  document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
    element.textContent = shown.length;
    element.hidden = shown.length === 0;
  });

  document.querySelectorAll('[data-wishlist-state]').forEach((element) => {
    element.toggleAttribute('data-filled', shown.length > 0);
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;

  event.preventDefault();
  toggle(button.dataset.wishlistToggle);
});

document.addEventListener('wishlist:change', sync);

// Twee tabbladen open: daar bewaart de één en kijkt de ander.
window.addEventListener('storage', (event) => {
  if (event.key === store.key) sync();
});

window.wishlist = { read: () => store.read(), sync };

sync();
